import { useQuery } from '@tanstack/react-query';
import type { WorkflowStatus } from '@scoutaround/script-breakdown-shared';

import { api, type Plan, type PlanFeatures, type PlanPrice, type ProjectSetup } from '../../lib/api.js';
import { AnalyseWithAi } from '../../components/AnalyseWithAi.js';
import { Callout } from '../../components/Callout.js';
import { Icon, type IconName } from '../../components/Icon.js';
import { money } from '../../components/Plans.js';
import { aiBillingMode, useProjectCredits } from '../../components/credits/useProjectCredits.js';
import type { Filter as SceneFilter } from './ReviewScenesStep.js';

const STATUS_LABEL: Record<WorkflowStatus, string> = {
  NOT_STARTED: 'Not started',
  IN_PROGRESS: 'In progress',
  COMPLETED: 'Completed',
};

/**
 * "YOUR SCENES ARE READY" — the choice between the two workflows (owner brief,
 * 2026-10-29).
 *
 * Scene validation is the shared foundation; what comes after it is two
 * independent pieces of work, and this is where the producer picks one, comes
 * back after finishing it, and picks the other. Each card carries the status the
 * server computed from that workflow's own rows, never a shared flag, so
 * finishing one leaves the other exactly where it was.
 *
 * NOTHING RUNS FROM HERE BY ITSELF. Choosing a workflow opens it; the AI reading
 * stays a button somebody presses. Two are offered here, both buttons with their
 * cost said on them: re-reading scenes that changed after the AI read them, and,
 * while the AI has not read the screenplay at all, the first reading.
 *
 * HOW TO START IS A CHOICE (2026-11-04). Before the first reading the page says
 * that the AI breakdown is paid per use from the production's credits, with the
 * balance and the way to buy them in the approval it opens, and that both
 * workflows can be done by hand at no cost. A free production is also offered
 * the paid plans, by what they add that the workspace can deliver (built,
 * code-gated features only: never a `surface_not_built` one).
 */
export function WorkflowChoiceStep({
  projectId,
  onLocations,
  onBreakdown,
  onReviewScenes,
  onWorkspace,
  onPlans,
}: {
  projectId: string;
  onLocations: () => void;
  onBreakdown: () => void;
  /** Back to the scene list, filtered where a notice points at some of them. */
  onReviewScenes: (filter: SceneFilter) => void;
  onWorkspace: () => void;
  /** Opens Plan & billing, where a plan is bought. Absent, no upgrade is offered. */
  onPlans?: () => void;
}) {
  const setup = useQuery({
    queryKey: ['project-setup', projectId],
    queryFn: () => api.projectSetup(projectId),
  });
  const current = useQuery({
    queryKey: ['current-script', projectId],
    queryFn: () => api.currentScript(projectId),
  });
  const versionId = current.data?.scriptVersionId ?? null;
  const validation = useQuery({
    queryKey: ['scene-validation', projectId, versionId],
    queryFn: () => api.sceneValidation(projectId, versionId as string),
    enabled: versionId !== null,
  });

  // The same keys as Plan & billing, so a payment there refreshes this page too.
  const billing = useQuery({
    queryKey: ['billing', projectId],
    queryFn: () => api.billingState(projectId),
  });
  const plans = useQuery({
    queryKey: ['billing-plans', projectId],
    queryFn: () => api.billingPlans(projectId),
  });
  const credits = useProjectCredits(projectId);
  const metered = aiBillingMode(credits.data) === 'METERED';

  const s = setup.data;
  const summary = validation.data?.summary;
  // Has the AI read this screenplay at all? The server's own test
  // (`SceneValidationService.view`): some scene carries a reading. Unknown
  // while the scenes load, so nothing is offered on a guess.
  const activeScenes = validation.data?.scenes;
  const neverRead =
    activeScenes !== undefined && activeScenes.length > 0 && activeScenes.every((scene) => scene.analysis === 'NOT_ANALYSED');
  // Writable and never paid: EXPIRED runs on the free features too, but is
  // read-only, and Plan & billing already leads with the way back for it.
  const upgrade = onPlans !== undefined && billing.data?.state === 'FREE' ? upgradeOffer(plans.data?.plans ?? []) : null;
  // Changed after the AI read them, or added with text after it read the
  // screenplay — the server's flag; this page only offers to read them.
  const toRead = (validation.data?.scenes ?? []).filter((scene) => scene.needsReading);
  const changed = toRead.filter((scene) => scene.analysis === 'OUT_OF_DATE').length;
  const added = toRead.length - changed;
  const unconfirmed = summary === undefined ? 0 : summary.toReview + summary.needsAttention;

  return (
    <div className="choice-page" data-testid="workflow-choice">
      <p className="kick">Script processing · Scenes confirmed</p>
      <h1 className="dh start-title" data-testid="workflow-choice-title">
        Your Scenes Are Ready
      </h1>
      <p className="start-lede">
        Your screenplay structure has been confirmed. Choose what you want to work on next.
      </p>

      <p className="choice-count" data-testid="workflow-choice-count">
        <Icon name="check" size={15} strokeWidth={2.6} />
        {summary === undefined
          ? 'Counting the confirmed scenes…'
          : `${String(summary.confirmed)} confirmed ${summary.confirmed === 1 ? 'scene' : 'scenes'}${
              summary.excluded > 0 ? ` · ${String(summary.excluded)} excluded` : ''
            }`}
      </p>

      {/* HOW TO START: WITH THE AI, OR BY HAND. Offered until the AI has read
          the screenplay once; after that the workflows read its proposals and
          the out-of-date notice below covers what changed. The AI side is the
          same control the workflow pages carry, so its approval (with the
          balance and "Buy credits") is the one every entry point uses. */}
      {neverRead ? (
        <section className="choice-start" data-testid="workflow-choice-start" aria-labelledby="workflow-choice-start-title">
          <h2 className="choice-start-title" id="workflow-choice-start-title">
            How do you want to start?
          </h2>
          <div className="choice-start-option" data-testid="workflow-choice-mode-ai">
            <p className="choice-start-label">{metered ? 'With AI · paid per use from project credits' : 'With AI'}</p>
            <AnalyseWithAi
              projectId={projectId}
              versionId={versionId}
              sceneCount={summary?.active}
              testId="workflow-choice-analyse"
            />
          </div>
          <div className="choice-start-option" data-testid="workflow-choice-mode-manual">
            <p className="choice-start-label">By hand · free</p>
            <div className="choice-start-manual">
              <p className="colh" style={{ margin: 0 }}>
                Break it down yourself
              </p>
              <p className="mm choice-start-manual-text">
                Open either workflow below and add the location requirements, the cast, props and the rest
                yourself. Nothing is charged, and you can still run the AI later from either workflow.
              </p>
            </div>
          </div>
        </section>
      ) : null}

      <div className="choice-cards">
        <WorkflowCard
          id="locations"
          icon="pin"
          title="Find Filming Locations"
          description="Analyze your screenplay to identify filming environments, organize location requirements across scenes, and discover suitable real-world locations."
          status={s?.workflows.locations ?? null}
          facts={s === undefined ? null : locationFacts(s)}
          cta="Continue to Locations"
          onOpen={onLocations}
        />
        <WorkflowCard
          id="breakdown"
          icon="script"
          title="Create Detailed Breakdown"
          description="Analyze scenes for cast, extras, props, costumes, vehicles, equipment, special effects, and other production requirements."
          status={s?.workflows.breakdown ?? null}
          facts={
            s === undefined
              ? null
              : `${String(s.breakdownReviewedScenes)} of ${String(s.sceneCount)} ${s.sceneCount === 1 ? 'scene' : 'scenes'} reviewed`
          }
          cta={
            s?.workflows.breakdown === 'IN_PROGRESS'
              ? 'Continue Breakdown'
              : s?.workflows.breakdown === 'COMPLETED'
                ? 'Open Breakdown'
                : 'Start Breakdown'
          }
          onOpen={onBreakdown}
        />
      </div>

      {/* AN UNCONFIRMED SCENE AFTER THE CONFIRMATION — added, split off, sent
          back to review, or from a newer draft. The workflows read every scene
          in the list, so it is said here, with the way to it, rather than
          discovered later. Below the choice: the choice is this page's job. */}
      {unconfirmed > 0 ? (
        <div style={{ marginTop: 18 }}>
          <Callout tone="warn" title={`${String(unconfirmed)} ${unconfirmed === 1 ? 'scene is' : 'scenes are'} not confirmed`} testId="workflow-choice-unconfirmed">
            Both workflows read every scene in the list, confirmed or not.{' '}
            <button
              type="button"
              className="intake-linkish"
              data-testid="workflow-choice-review-unconfirmed"
              onClick={() => {
                onReviewScenes(summary !== undefined && summary.needsAttention > 0 ? 'NEEDS_ATTENTION' : 'TO_REVIEW');
              }}
            >
              Review them
            </button>
          </Callout>
        </div>
      ) : null}

      {/* SCENES THE AI'S READING DOES NOT COVER AS THEY ARE NOW: changed since
          it read them (their proposals are kept, never erased, but not
          presented as current), or added with text after it read the
          screenplay — a scene typed by hand is analysable, so it is offered.
          Reading exactly those is a button, never started by itself. */}
      {toRead.length > 0 ? (
        <div style={{ marginTop: 18 }} data-testid="workflow-choice-out-of-date">
          <Callout
            tone="warn"
            title={
              added === 0
                ? `${String(changed)} ${changed === 1 ? 'scene changed' : 'scenes changed'} after the AI read ${changed === 1 ? 'it' : 'them'}`
                : changed === 0
                  ? `${String(added)} ${added === 1 ? 'scene was' : 'scenes were'} added after the AI read the screenplay`
                  : `${String(toRead.length)} scenes changed or were added after the AI read the screenplay`
            }
            testId="workflow-choice-out-of-date-callout"
          >
            {`Scene${toRead.length === 1 ? '' : 's'} ${toRead
              .slice(0, 8)
              .map((scene) => scene.sceneNumber ?? String(scene.ordinal))
              .join(', ')}${toRead.length > 8 ? '…' : ''}. ${
              changed > 0
                ? 'What the AI proposed for a changed scene is kept, but describes the scene as it was. '
                : ''
            }${added > 0 ? 'An added scene has not been read at all. ' : ''}Read ${toRead.length === 1 ? 'it' : 'them'} so both workflows work from the scenes as they are now.`}
          </Callout>
          <div style={{ marginTop: 10 }}>
            <AnalyseWithAi
              projectId={projectId}
              versionId={versionId}
              sceneCount={summary?.active}
              sceneIds={toRead.map((scene) => scene.id)}
              testId="workflow-choice-reanalyse"
            />
          </div>
        </div>
      ) : null}

      {/* THE UPGRADE OFFER, on a writable free production only. What it lists is
          read from the plans the server sells, so a plan edited in the admin
          says what it now has; the price is a one-month term's, billed once. */}
      {upgrade !== null ? (
        <div className="choice-upgrade" data-testid="workflow-choice-upgrade">
          <div className="choice-upgrade-body">
            <p className="colh" style={{ margin: 0 }}>
              Upgrade this production
            </p>
            <p className="mm choice-upgrade-text" data-testid="workflow-choice-upgrade-text">
              {`This production is on the free plan. ${upgrade.adds}${
                upgrade.from === null ? '' : ` Paid plans start at ${upgrade.from} for a month.`
              }${metered ? ' The first paid plan also comes with initial AI credits for this production.' : ''}`}
            </p>
          </div>
          <button type="button" className="btn choice-upgrade-cta" data-testid="workflow-choice-see-plans" onClick={onPlans}>
            See plans
            <Icon name="arrowRight" size={15} />
          </button>
        </div>
      ) : null}

      <p className="mm choice-shared" data-testid="workflow-choice-shared">
        Both work from the same confirmed scenes, in either order. Starting one never runs or completes
        the other. One AI reading serves both: it proposes the location requirements and lists the cast,
        and it only runs when you press it.
      </p>

      <div className="start-actions choice-actions">
        <button
          type="button"
          className="btn btn-ghost"
          data-testid="workflow-choice-back"
          onClick={() => {
            onReviewScenes('ALL');
          }}
        >
          <Icon name="arrowLeft" size={15} />
          Back to scenes
        </button>
        <button type="button" className="ctl" data-testid="workflow-choice-workspace" onClick={onWorkspace}>
          Open the workspace
        </button>
      </div>
    </div>
  );
}

/** What the Locations workflow has, in its own numbers. */
function locationFacts(s: ProjectSetup): string {
  if (s.workflows.locations === 'NOT_STARTED') return 'No location requirements yet';

  return `${String(s.approvedCount)} approved · ${String(s.searchedCount)} searched · ${String(s.matchedLocationCount)} ${
    s.matchedLocationCount === 1 ? 'place' : 'places'
  } matched`;
}

/**
 * What a paid plan adds that the workspace DELIVERS today. Each of these is a
 * code gate in `docs/contracts/workspace-plans.json`; the market insights and
 * the exclusive locations are sold but have no surface (`surface_not_built`),
 * so they are never offered here.
 */
const UPGRADE_FEATURES: readonly { key: keyof PlanFeatures; label: string }[] = [
  { key: 'collaboration', label: 'team collaboration' },
  { key: 'mediaStorage', label: 'file and media storage' },
  { key: 'scheduling', label: 'the shoot schedule' },
];

/**
 * The upgrade sentence, from the plans the server sells, or null when there is
 * nothing true to say: no paid plan, or none adds a delivered feature.
 */
export function upgradeOffer(plans: readonly Plan[]): { adds: string; from: string | null } | null {
  const paid = plans.filter((plan) => !plan.isFree);
  if (paid.length === 0) return null;

  const parts = UPGRADE_FEATURES.flatMap(({ key, label }) => {
    const on = paid.filter((plan) => plan.features[key] === true).length;
    if (on === 0) return [];

    return [on === paid.length ? label : `${label} (on some plans)`];
  });
  if (parts.length === 0) return null;

  const cheapest = paid
    .flatMap((plan) => plan.prices.filter((price) => price.termMonths === 1))
    .reduce<PlanPrice | null>((low, price) => (low === null || price.amountMinor < low.amountMinor ? price : low), null);

  return {
    adds: `A paid plan adds ${joinList(parts)}.`,
    from: cheapest === null ? null : money(cheapest.amountMinor, cheapest.currency),
  };
}

function joinList(parts: readonly string[]): string {
  if (parts.length <= 1) return parts.join('');

  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1] as string}`;
}

function WorkflowCard({
  id,
  icon,
  title,
  description,
  status,
  facts,
  cta,
  onOpen,
}: {
  id: 'locations' | 'breakdown';
  icon: IconName;
  title: string;
  description: string;
  status: WorkflowStatus | null;
  facts: string | null;
  cta: string;
  onOpen: () => void;
}) {
  return (
    <article className="choice-card" data-testid={`workflow-card-${id}`} data-status={status ?? ''}>
      <div className="choice-card-head">
        <span className="choice-card-icon" aria-hidden="true">
          <Icon name={icon} size={20} />
        </span>
        {status === null ? null : (
          <span className="choice-status" data-status={status} data-testid={`workflow-status-${id}`}>
            {status === 'COMPLETED' ? <Icon name="check" size={12} strokeWidth={2.8} /> : null}
            {STATUS_LABEL[status]}
          </span>
        )}
      </div>
      <h2 className="choice-card-title">{title}</h2>
      <p className="choice-card-text">{description}</p>
      {facts === null ? null : (
        <p className="choice-card-facts" data-testid={`workflow-facts-${id}`}>
          {facts}
        </p>
      )}
      <button type="button" className="btn choice-card-cta" data-testid={`workflow-open-${id}`} onClick={onOpen}>
        {cta}
        <Icon name="arrowRight" size={15} />
      </button>
    </article>
  );
}
